import { Button, Input, Select, Form, Space, Row, Col, Collapse } from "antd";
import { ReactNode, useEffect, useState } from "react";
import { useAppSelector } from "../../redux/hooks";
import { useGetBrandsQuery } from "../../redux/services/brandApi";
import { useGetLocationsQuery } from "../../redux/services/locationsApi";
import { useGetUsersQuery } from "../../redux/services/userApi";
import { useGetPartnersQuery } from "../../redux/services/partnerApi";
import { OldInstallTypeOptions } from "../../constants/helperConstants";
import { useNavigate } from "react-router";
import { getQuerySearchParams } from "../../utils";

const { Option } = Select;

const labelStyle = {
  width: "240px",
  color: "rgba(0, 0, 0, 0.65)",
  cursor: "auto",
  paddingLeft: 8
};

const FilterField = ({
  label,
  name,
  children
}: {
  label: string;
  name: string;
  children: ReactNode;
}) => (
  <Form.Item label="">
    <Space.Compact style={{ width: "100%" }}>
      <Input disabled value={label} style={labelStyle} />
      <Form.Item name={name} noStyle>
        {children}
      </Form.Item>
    </Space.Compact>
  </Form.Item>
);

type FilterItem = {
  name: string;
  show: boolean;
  group?: string;
  field: ReactNode;
};

const COLUMN_COUNT = 3;

const layoutFilters = (visible: FilterItem[]): FilterItem[] => {
  const queue = [...visible];
  const ordered: FilterItem[] = [];

  while (queue.length) {
    const column = ordered.length % COLUMN_COUNT;
    const next = queue[0];
    const startsPair = next.group !== undefined && queue[1]?.group === next.group;

    let index = 0;
    if (startsPair && column === COLUMN_COUNT - 1) {
      const filler = queue.findIndex((item) => item.group === undefined);
      if (filler > 0) index = filler;
    }

    ordered.push(...queue.splice(index, 1));
  }

  return ordered;
};

const byChildren = (input: string, option: any) =>
  option.props.children.toLowerCase().indexOf(input.toLowerCase()) >= 0;

const byLabel = (input: string, option: any) =>
  option.label.toLowerCase().includes(input.toLowerCase());

const Filters = (props: any) => {
  const { applyFilters, bookings, installations, surveys, visits } = props;
  const [form] = Form.useForm();

  const { data: brandsData } = useGetBrandsQuery({ all_pages: true });
  const brands = brandsData?.brands || [];

  const { data: partnersData } = useGetPartnersQuery({ all_pages: true });
  const partners = partnersData?.partners || [];

  const navigate = useNavigate();

  const { data: cityLocationData } = useGetLocationsQuery({
    location_type: "city",
    all_pages: true
  });
  const cityLocations = cityLocationData?.locations || [];

  const { data: zoneLocationsData } = useGetLocationsQuery({
    location_type: "zone",
    all_pages: true
  });
  const zones = zoneLocationsData?.locations;

  const zoneOptions = zones?.map((zone: any) => ({ value: zone.id, label: zone.name })) || [];

  const { data: stateLocationsData } = useGetLocationsQuery({
    location_type: "state",
    all_pages: true
  });
  const states = stateLocationsData?.locations;

  const { data: fieldAgentData } = useGetUsersQuery({ role_type: "field_agent", all_pages: true });
  const fieldAgents = fieldAgentData?.users || [];

  const { data: cityManagerData } = useGetUsersQuery({
    role_type: "city_manager",
    all_pages: true
  });
  const cityManagers = cityManagerData?.users || [];

  const user = useAppSelector<any>((store) => store.auth.user);

  const isClient = user?.user_type === "client";

  const [filtersVisible, setFiltersVisible] = useState(true);

  const querySearchParams = getQuerySearchParams();

  useEffect(() => {
    const cleanFieldValues = Object.fromEntries(
      Object.entries(querySearchParams).filter(
        ([_, value]) => value !== null && value !== undefined && value !== ""
      )
    );
    form.setFieldsValue(cleanFieldValues)
    onApply(cleanFieldValues)
  }, []);

  const onReset = () => {
    form.resetFields();
    navigate(location.pathname);
    applyFilters(form.getFieldsValue());
  };

  const onApply = (values: any) => {
    const params = new URLSearchParams();
    Object.entries(values).map((key) => {
      const fieldValue = form.getFieldValue(key[0]);
      if (Array.isArray(fieldValue)) {
        if (fieldValue.length > 0) {
          params.set(key[0], fieldValue.join(","));
        } else {
          params.delete(key[0]);
        }
      } else if (fieldValue !== undefined && fieldValue !== "") {
        params.set(key[0], fieldValue);
      }
      navigate(`?${params.toString()}`);
    });
    applyFilters(values);
  };

  const onFinishFailed = (errorInfo: any) => {
    console.log(errorInfo);
  };

  const showApprovalDates = installations || surveys;

  const filterFields: FilterItem[] = [
    {
      name: "customer_name",
      show: true,
      field: (
        <FilterField label="Name" name="customer_name">
          <Input placeholder="Enter Name" />
        </FilterField>
      )
    },
    {
      name: "brand_id",
      show: !isClient,
      field: (
        <FilterField label="Brand" name="brand_id">
          <Select
            mode="multiple"
            placeholder="Select brand"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {brands?.map((brand: any) => (
              <Option key={brand?.name} value={brand?.id}>
                {brand?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    },
    {
      name: "assigned_city_manager_id",
      show: !isClient,
      field: (
        <FilterField label="City Manager" name="assigned_city_manager_id">
          <Select
            mode="multiple"
            placeholder="Select city manager"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {cityManagers?.map((manager: any) => (
              <Option key={manager?.id} value={manager?.id}>
                {manager?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    },
    {
      name: "contact_number",
      show: true,
      field: (
        <FilterField label="Contact Number" name="contact_number">
          <Input placeholder="Enter Contact Number" />
        </FilterField>
      )
    },
    {
      name: "city_id",
      show: true,
      field: (
        <FilterField label="City" name="city_id">
          <Select
            mode="multiple"
            placeholder="Select city"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {cityLocations?.map((city: any) => (
              <Option key={city?.name} value={city?.id}>
                {city?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    },
    {
      name: "zone_id",
      show: true,
      field: (
        <FilterField label="Zone" name="zone_id">
          <Select
            mode="multiple"
            placeholder="Select zone"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byLabel}
            options={zoneOptions}
          />
        </FilterField>
      )
    },
    {
      name: "status",
      show: true,
      field: (
        <FilterField label="Status" name="status">
          <Select
            mode="multiple"
            placeholder="Select status"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            <Option value="draft">Draft</Option>
            <Option value="waiting_schedule">Waiting Schedule</Option>
            <Option value="scheduled">Scheduled</Option>
            <Option value="in_progress">In Progress</Option>
            <Option value="cancelled">Cancelled</Option>
            <Option value="completed">Completed</Option>
            <Option value="on_hold">On Hold</Option>
            <Option value="pending_approval">Pending For Approval</Option>
            <Option value="is present">Is Present</Option>
            <Option value="is blank">Is Blank</Option>
          </Select>
        </FilterField>
      )
    },
    {
      name: "start_date",
      show: true,
      group: "date",
      field: (
        <FilterField label={`${bookings ? "Creation" : (surveys || installations || visits) ? "Scheduled" : "Start"} Date (From)`} name="start_date">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "end_date",
      show: true,
      group: "date",
      field: (
        <FilterField label={`${bookings ? "Creation" : (surveys || installations || visits) ? "Scheduled" : "End"} Date (To)`} name="end_date">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "brand_booking_status",
      show: true,
      field: (
        <FilterField label="Brand Booking Status" name="brand_booking_status">
          <Select
            mode="multiple"
            placeholder="Select status"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            <Option value="booked">Booked</Option>
            <Option value="invoiced">Invoiced</Option>
            <Option value="delivered">Delivered</Option>
            <Option value="intend_to_cancel">Intend To Cancel</Option>
            <Option value="cancelled">Cancelled</Option>
          </Select>
        </FilterField>
      )
    },
    {
      name: "start_approved_at",
      show: showApprovalDates,
      group: "approval",
      field: (
        <FilterField label="Approval Date (From)" name="start_approved_at">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "end_approved_at",
      show: showApprovalDates,
      group: "approval",
      field: (
        <FilterField label="Approval Date (To)" name="end_approved_at">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "brand_booking_code",
      show: true,
      field: (
        <FilterField label="Brand Booking Code" name="brand_booking_code">
          <Input placeholder="Enter Brand Booking Code" />
        </FilterField>
      )
    },
    {
      name: "next_follow_up_start",
      show: !isClient,
      group: "followUp",
      field: (
        <FilterField label="Follow up date (From)" name="next_follow_up_start">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "next_follow_up_end",
      show: !isClient,
      group: "followUp",
      field: (
        <FilterField label="Follow up date (To)" name="next_follow_up_end">
          <Input type="date" />
        </FilterField>
      )
    },
    {
      name: "ecb_code",
      show: true,
      field: (
        <FilterField label="Booking/Survey Code" name="ecb_code">
          <Input placeholder="Enter eCB Code" />
        </FilterField>
      )
    },
    {
      name: "partner_id",
      show: !isClient,
      field: (
        <FilterField label="Partner" name="partner_id">
          <Select
            mode="multiple"
            placeholder="Select partner"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {partners?.map((partner: any) => (
              <Option key={partner?.name} value={partner?.id}>
                {partner?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    },
    {
      name: "agent_id",
      show: !isClient,
      field: (
        <FilterField label="Agent" name="agent_id">
          <Select
            mode="multiple"
            placeholder="Select Agent"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {fieldAgents?.map((agent: any) => (
              <Option key={agent?.id} value={agent?.id}>
                {agent?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    },
    {
      name: "is_first_contact",
      show: true,
      field: (
        <FilterField label="Is First Contact ?" name="is_first_contact">
          <Select
            placeholder="Select status"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            <Option value="yes">Yes</Option>
            <Option value="no">No</Option>
          </Select>
        </FilterField>
      )
    },
    {
      name: "install_type",
      show: installations && !isClient,
      field: (
        <FilterField label="Install Type" name="install_type">
          <Select
            mode="multiple"
            placeholder="Select Install Type"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byLabel}
            options={OldInstallTypeOptions}
          />
        </FilterField>
      )
    },
    {
      name: "state_id",
      show: bookings || surveys || installations,
      field: (
        <FilterField label="State" name="state_id">
          <Select
            mode="multiple"
            placeholder="Select state"
            allowClear
            showSearch
            optionFilterProp="children"
            filterOption={byChildren}
          >
            {states?.map((state: any) => (
              <Option key={state?.id} value={state?.id}>
                {state?.name}
              </Option>
            ))}
          </Select>
        </FilterField>
      )
    }
  ];

  const visitCompletedDateRange = [{
    name: "visit_completed_from",
    show: true,
    group: "date",
    field: (
      <FilterField label="Completion Date (From)" name="visit_completed_from">
        <Input type="date" />
      </FilterField>
    )
  },
  {
    name: "visit_completed_to",
    show: true,
    group: "date",
    field: (
      <FilterField label="Completion Date (To)" name="visit_completed_to">
        <Input type="date" />
      </FilterField>
    )
  }]

  if (visits)
    filterFields.splice(10, 0, ...visitCompletedDateRange)

  return (
    <Collapse
      activeKey={filtersVisible ? ["1"] : []}
      onChange={(keys) => setFiltersVisible(keys.length > 0)}
      style={{ marginBottom: 20 }}
    >
      <Collapse.Panel
        header={
          <span style={{ fontWeight: 600, fontSize: 16 }}>
            {filtersVisible ? "Hide Filters" : "Show Filters"}
          </span>
        }
        key="1"
      >
        <Form
          name="filter_form"
          form={form}
          onFinish={onApply}
          onFinishFailed={onFinishFailed}
          autoComplete="off"
        >
          <Row gutter={16}>
            {layoutFilters(filterFields.filter((filter) => filter.show)).map((filter) => (
              <Col span={8} key={filter.name}>
                {filter.field}
              </Col>
            ))}
          </Row>
          <div style={{ display: "flex", gap: 6 }}>
            <Button type="default" onClick={onReset} style={{ color: "green" }}>
              Clear Filter
            </Button>
            <Button form="filter_form" key="submit" htmlType="submit" type="primary">
              Apply Filter
            </Button>
          </div>
        </Form>
      </Collapse.Panel>
    </Collapse>
  );
};

export default Filters;
